import { UnitTransferJointDematCheckStatusEnumType } from '__generated__/graphql';
import { FC, ReactNode } from 'react';

import Badge from 'primitives/Badge';

import SectionCard from 'components/SectionCard';

import unitTransferJointDematCheckStatusToReadable from 'utils/enums/unit-transfer-joint-demat-check-status-to-readable';
import unitTransferSlaToBadge from 'utils/enums/unit-transfer-sla-to-badge';

import { UnitTransferBlockers } from './derive-blockers';

const Metric: FC<{ label: string; value: ReactNode; detail?: ReactNode }> = ({
  label,
  value,
  detail,
}) => (
  <div className="flex flex-col gap-1">
    <p className="text-sm text-gray-500">{label}</p>
    <p className="text-lg font-semibold leading-6 text-gray-900">{value}</p>
    {detail ? <div className="flex items-center gap-2">{detail}</div> : null}
  </div>
);

/**
 * The four questions ops asks before touching a request: how far along are the documents, is
 * anything waiting on a human verdict, how much of the signing chain is done, and is it late.
 */
const AtAGlancePanel: FC<{
  documentsApproved: number;
  documentsRequired: number;
  blockers: UnitTransferBlockers;
  slaDaysRemaining?: number | null;
  jointDematCheckStatus: UnitTransferJointDematCheckStatusEnumType;
}> = ({
  documentsApproved,
  documentsRequired,
  blockers,
  slaDaysRemaining,
  jointDematCheckStatus,
}) => {
  const slaBadge = unitTransferSlaToBadge(slaDaysRemaining);
  const jointDemat = unitTransferJointDematCheckStatusToReadable(jointDematCheckStatus);

  return (
    <SectionCard title="At a glance">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Documents approved" value={`${documentsApproved} of ${documentsRequired}`} />
        <Metric
          label="AI checks awaiting review"
          value={blockers.checksAwaitingReview}
          detail={
            blockers.checksAwaitingReview > 0 ? (
              <Badge label="Blocks completion" color="amber" />
            ) : (
              <Badge label="Clear" color="green" />
            )
          }
        />
        <Metric
          label="Signatures"
          value={
            blockers.signaturesTotal === 0
              ? 'Not initiated'
              : `${blockers.signaturesSigned} of ${blockers.signaturesTotal} signed`
          }
        />
        <Metric
          label="SLA"
          value={slaBadge ? <Badge label={slaBadge.label} color={slaBadge.color} /> : '—'}
          detail={<Badge label={`Joint demat: ${jointDemat.label}`} color={jointDemat.color} />}
        />
      </div>
    </SectionCard>
  );
};

export default AtAGlancePanel;
